import { useGridFilter } from 'ag-grid-react';
import { Button, ButtonGroup, Checkbox, TextField } from '@seeqdev/qomponents';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GridApi, ISetFilterParams } from 'ag-grid-community';
import { makeNodeValueGetter } from '@/vantage/components/evidence_table/helpers/column.utilities';
import {
  FILTER_MODES,
  FilterMode,
  type FilterModel,
  type FilterPrimitive,
  keyForValue,
} from './multiModalColumnFilter.utilities';
import {
  isModeDisabled,
  useActiveGroupings,
  useAvailableValues,
  useFilterPass,
  useFilterState,
  useModelSerializer,
  useValueFormatter,
} from './multiModalColumnFilter.hooks';
import type { Maybe } from '@/utilities.types';

interface GroupableColumnFilterProps {
  model: Maybe<FilterModel>;
  onModelChange: (model: FilterModel | null) => void;
  colDef: { field: string };
  api: GridApi;
  filterParams?: ISetFilterParams;
}

export const MultiModalColumnFilter = ({
  model,
  onModelChange,
  colDef,
  api,
  ...filterParams
}: GroupableColumnFilterProps): JSX.Element => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');

  // Resolve effective filterParams: prefer prop, else read from columnDef
  const effectiveFilterParams: ISetFilterParams = {
    ...(api.getColumn(colDef.field)?.getColDef()?.filterParams ?? {}),
    ...(filterParams ?? {}),
  } as ISetFilterParams;

  const getValueForNode = useMemo(() => makeNodeValueGetter(api, colDef.field), [api, colDef.field]);

  const { filterMode, selectedValues, handleFilterModeChange, handleValueToggle, handleClear, handleSelectAll } =
    useFilterState({ model, onModelChange });

  const groupings = useActiveGroupings(api);

  const availableValues: FilterPrimitive[] = useAvailableValues({
    api,
    field: colDef.field,
    filterMode,
    getValueForNode,
    filterParams: effectiveFilterParams,
  });

  const doesFilterPass = useFilterPass({
    model,
    api,
    field: colDef.field,
    getValueForNode,
  });

  const { afterGuiAttached, getModelAsString } = useModelSerializer({
    api,
    field: colDef.field,
    filterParams: effectiveFilterParams,
    getActiveGroupings: () => groupings,
  });

  const valueFormatter = useValueFormatter({
    api,
    field: colDef.field,
    filterParams: effectiveFilterParams,
  });

  // Filter available values based on search text
  const trimmedSearchText = searchText.trim().toLowerCase();
  const filteredValues =
    trimmedSearchText === ''
      ? availableValues
      : availableValues.filter((value) => valueFormatter(value).toLowerCase().includes(trimmedSearchText));

  // Register with AG Grid
  useGridFilter({
    doesFilterPass,
    afterGuiAttached,
    getModelAsString,
  });

  // Render UI
  return (
    <div
      className="tw:min-w-[240px] tw:max-w-[320px] tw:flex tw:flex-col tw:gap-1 tw:min-h-0"
      style={{ contain: 'layout paint', scrollbarGutter: 'stable' }}
      data-testid="multimodal-filter-root">
      <div className="tw:flex tw:flex-col tw:gap-1 tw:px-2 tw:pt-1.5 tw:pb-2">
        <div className="tw:font-semibold tw:text-s">{t('VANTAGE.EVIDENCE_TABLE.FILTERS.FILTER_MODE')}</div>
        <ButtonGroup
          buttons={FILTER_MODES.map((mode) => ({
            variant: 'button' as const,
            buttonProps: {
              label: t(`VANTAGE.EVIDENCE_TABLE.FILTERS.${mode.toUpperCase()}`),
              value: mode,
              variant: 'outline' as const,
              size: 'sm' as const,
              isActive: mode === filterMode,
              disabled: isModeDisabled({ mode, groupings }),
              extraClassNames: 'tw:px-2 tw:text-s',
            },
          }))}
          onChange={(value) => handleFilterModeChange(value as FilterMode)}
        />
      </div>

      <div className="tw:flex tw:flex-col tw:gap-1 tw:px-2">
        <div className="tw:font-semibold tw:text-s">{t('VANTAGE.EVIDENCE_TABLE.FILTERS.SELECT_VALUES')}</div>
        <TextField
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={t('VANTAGE.EVIDENCE_TABLE.FILTERS.SEARCH_PLACEHOLDER')}
          extraClassNames="tw:text-s tw:py-1 tw:h-7"
          testId="multimodal-filter-search"
        />
      </div>

      <div
        className="tw:flex tw:flex-col tw:max-h-[300px] tw:overflow-y-auto tw:px-2 tw:min-h-0"
        style={{ contain: 'layout paint' }}>
        {filteredValues.map((value: FilterPrimitive) => (
          <div key={keyForValue(value)} className="hover:tw:bg-gray-50" data-testid="multimodal-filter-option">
            <Checkbox
              checked={selectedValues.has(value)}
              onChange={() => handleValueToggle(value)}
              label={valueFormatter(value)}
              extraLabelClassNames="tw:text-s"
              testId="multimodal-filter-option-checkbox"
            />
          </div>
        ))}
      </div>

      <div className="tw:flex tw:gap-1 tw:justify-between tw:px-2 tw:pb-1.5">
        <Button
          size="sm"
          label={t('VANTAGE.EVIDENCE_TABLE.FILTERS.SELECT_ALL')}
          variant="outline"
          onClick={() => handleSelectAll(filteredValues)}
          disabled={filteredValues.length === 0}
        />
        <Button
          size="sm"
          label={t('CLEAR')}
          extraClassNames="btn-seeq"
          variant="theme"
          onClick={handleClear}
          disabled={selectedValues.size === 0 && filterMode === FilterMode.Default}
        />
      </div>
    </div>
  );
};

export default MultiModalColumnFilter;
